import { AnimatePresence } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ProductOverlay } from 'src/shared/components/SaraChat/ProductOverlay'
import type { ProductItem } from 'src/shared/components/SaraChat/message_components/ProductSelectionMessage/types'
import { useRequestContext } from '../hooks/RequestContext'
import {
  useConversationByQuoteId,
  useCreateConversation,
} from 'src/api/db/apiConversation'
import {
  useMessages,
  type OrderMessageWithUser,
} from 'src/api/db/apiOrderMessage'
import { queryKeys } from 'src/api/db/queryKeys'
import { trackEvent } from 'src/api/db/apiAnalytics'
import { useSupabaseClient } from 'src/api/supabase'
import { useOrderNotifications } from 'src/api/server/useOrderNotifications'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { ChatInputForm } from 'src/shared/components/GlobalChatWidget/components/ChatInputForm'
import type { ChatInputTabSuggestion } from 'src/shared/components/GlobalChatWidget/components/ChatInputForm'
import { isSaraPrefillOnly } from 'src/shared/components/GlobalChatWidget/components/saraPrefill'
import { Column } from 'src/shared/components/Semantic/all'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { ONBOARDING_ELEMENT_IDS } from 'src/shared/hooks/useOnboarding'
import { GuestSignupDialog } from './Chat/components/GuestSignupDialog'
import { PingKlayderTeamCta } from './Chat/components/PingKlayderTeamCta'
import { useRequestSaraChat } from './Chat/hooks/useRequestSaraChat'
import {
  useRequestChatDraftStore,
  useRequestChatStickyRecipient,
} from './Chat/hooks'
import {
  detectChatRecipient,
  isExplicitSaraText,
  resolveStickyRecipient,
} from './Chat/utils/detectChatRecipient'
import {
  isAdminSaraContent,
  withSaraMessagePrivacy,
} from './Chat/utils/privateSaraMessage'
import {
  createTeamPingMessageContent,
  isTeamPingMessage,
} from './Chat/utils/teamPingMessage'
import { escapeHtml, stripHtml } from './Chat/utils'
import { RequestChatView } from './RequestChatView'
import { parseSaraMessageContent, isSuccessfulMockupGeneration } from './RequestSaraMessage'
import {
  extractAssistantQuickReply,
  getLatestSaraGroupMessages,
  resolveSaraQuickAction,
  SARA_QUICK_ACTION_LABELS,
  SARA_QUICK_ACTION_PHRASES,
  saraGroupFetchedLogoUrls,
  type SaraQuickActionKind,
} from './saraQuickActions'
import { MockupLeadPopup } from './Chat/components/MockupLeadPopup'
import { buildAppRequestPath } from 'src/routes/paths'
import { markConversationSeen } from 'src/shared/utils/adminInboxReadState'
import {
  CONTACTED_SALES_STATUS,
  isDraftQuoteStatus,
} from 'src/shared/consts/quoteStatus'

export type RequestChatChannel = 'customer' | 'admin'

export const RequestMessagesTab = ({
  channel = 'customer',
}: {
  channel?: RequestChatChannel
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { session } = useAuth()
  const isAdmin = useIsAdmin()
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const orderNotifications = useOrderNotifications()
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const location = useLocation()
  const navigate = useNavigate()
  const seededInitialMessage = useRef<string | null>(null)

  const { publicId, quote, quoteItems, isQuotePending } = useRequestContext()
  const { conversation, isPending: isPendingConversation } =
    useConversationByQuoteId(quote?.id)
  const [localConversationId, setLocalConversationId] = useState<number | null>(
    null
  )
  const conversationId = conversation?.id ?? localConversationId
  const [productOverlay, setProductOverlay] = useState<{
    products: ProductItem[]
    quote_id: number
    product_categories?: string[]
  } | null>(null)
  const [accountGateOpen, setAccountGateOpen] = useState(false)

  useEffect(() => {
    if (conversation?.id) {
      setLocalConversationId(conversation.id)
    }
  }, [conversation?.id])

  const { messages, isPending: isPendingMessages } = useMessages(conversationId)
  const isLoadingMessages = conversationId ? isPendingMessages : false

  useEffect(() => {
    if (!isAdmin || !conversationId || messages.length === 0) return
    const latest = messages[messages.length - 1]
    markConversationSeen(conversationId, latest.created_at)
  }, [isAdmin, conversationId, messages])

  const { createConversation } = useCreateConversation()
  const [isSending, setIsSending] = useState(false)

  const isAdminChannel = isAdmin && channel === 'admin'
  const [customerDraft, setCustomerDraft] = useState('')
  const [adminDraft, setAdminDraft] = useState('')
  const draft = isAdminChannel ? adminDraft : customerDraft
  const setDraft = isAdminChannel ? setAdminDraft : setCustomerDraft
  const {
    notes: localNotes,
    addNote: addLocalNote,
    clearNotes: clearLocalNotes,
  } = useRequestChatDraftStore(publicId)
  const { stickyRecipient, setStickyRecipient } =
    useRequestChatStickyRecipient(publicId)

  const {
    chatId: saraChatId,
    saraMessages,
    isSaraLoading,
    sendToSara,
    stopSara,
  } = useRequestSaraChat({
    quoteId: quote?.id,
    orgId: quote?.org_id ?? undefined,
  })

  const lastQuoteCrudMessageId = useRef<number | null>(null)
  const lastMockupFingerprint = useRef<string | null>(null)

  useEffect(() => {
    if (!quote?.id) return

    const quoteCrudMessage = [...saraMessages]
      .reverse()
      .find((message) => {
        if (message.role !== 'system') return false
        const content =
          typeof message.content === 'string'
            ? JSON.parse(message.content || '{}')
            : message.content ?? {}
        return content.tool_result === 'quote_crud'
      })

    if (!quoteCrudMessage?.id) return
    if (lastQuoteCrudMessageId.current === quoteCrudMessage.id) return

    lastQuoteCrudMessageId.current = quoteCrudMessage.id
    void queryClient.refetchQueries({
      queryKey: [queryKeys.quoteItems, quote.id],
    })
    void queryClient.invalidateQueries({
      queryKey: [queryKeys.quotes],
    })
  }, [quote?.id, queryClient, saraMessages])

  useEffect(() => {
    if (!quote?.id) return

    const mockupMessages = saraMessages
      .filter((message) => message.role === 'system')
      .map((message) => ({
        message,
        content: parseSaraMessageContent(message),
      }))
      .filter(({ content }) => content.tool_result === 'mockup_generation')
      .sort((a, b) => a.message.id - b.message.id)

    const latest = mockupMessages.at(-1)
    if (!latest?.message.id) return

    const readyCount = (latest.content.data?.results ?? []).filter(
      (result: { success?: boolean; mockup_urls?: string[] }) =>
        result.success && (result.mockup_urls?.length ?? 0) > 0
    ).length
    const fingerprint = `${latest.message.id}:${latest.content.pending ? 'p' : 'd'}:${readyCount}`
    if (lastMockupFingerprint.current === fingerprint) return

    lastMockupFingerprint.current = fingerprint
    void queryClient.refetchQueries({
      queryKey: [queryKeys.quoteItems, quote.id],
    })
    void queryClient.invalidateQueries({
      queryKey: [queryKeys.quotes],
    })
  }, [quote?.id, queryClient, saraMessages])

  const onOpenProductOverlay = useCallback(
    (
      products: ProductItem[],
      quote_id: number,
      product_categories?: string[]
    ) => {
      setProductOverlay({ products, quote_id, product_categories })
    },
    []
  )

  const onCloseProductOverlay = useCallback(() => {
    setProductOverlay(null)
  }, [])

  useEffect(() => {
    if (!publicId) return

    const locationState = location.state as {
      initialChatMessage?: string
      chatResetAt?: number
    } | null
    const initialChatMessage = locationState?.initialChatMessage?.trim()

    const pendingSaraNote = localNotes.find((note) => note.recipient === 'sara')
    const sourceText = (
      initialChatMessage ||
      pendingSaraNote?.plainText ||
      ''
    ).trim()

    if (!sourceText) return

    const seedKey = `${publicId}:${locationState?.chatResetAt ?? ''}:${sourceText}`
    if (seededInitialMessage.current === seedKey) return

    const { recipient, cleanedText, nextSticky, isPrivate } =
      detectChatRecipient(sourceText, stickyRecipient)

    if (recipient === 'sara') {
      if (!quote?.id) return

      seededInitialMessage.current = seedKey
      setStickyRecipient(nextSticky)

      void sendToSara(cleanedText, undefined, { isPrivate })
        .then(() => {
          clearLocalNotes()
        })
        .catch((err) => {
          console.error('Request Sara send error:', err)
          toast({
            variant: 'destructive',
            title: t('Failed', 'Misslyckades'),
            description: err instanceof Error ? err.message : undefined,
          })
        })

      if (initialChatMessage) {
        navigate(`${location.pathname}${location.search}`, {
          replace: true,
          state: locationState?.chatResetAt
            ? { chatResetAt: locationState.chatResetAt }
            : {},
        })
      }
      return
    }

    if (!initialChatMessage) return

    seededInitialMessage.current = seedKey

    const alreadyHasNote = localNotes.some(
      (note) => note.plainText === initialChatMessage
    )
    if (!alreadyHasNote) {
      addLocalNote({
        content: `<p>${escapeHtml(initialChatMessage)}</p>`,
        plainText: initialChatMessage,
        recipient: 'sales',
      })
    }

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: locationState?.chatResetAt
        ? { chatResetAt: locationState.chatResetAt }
        : {},
    })
  }, [
    addLocalNote,
    clearLocalNotes,
    localNotes,
    location.pathname,
    location.search,
    location.state,
    navigate,
    publicId,
    quote?.id,
    sendToSara,
    setStickyRecipient,
    stickyRecipient,
    t,
    toast,
  ])

  const hasCustomerMessages = useMemo(
    () => messages.some((message) => message.role === 'customer'),
    [messages]
  )

  const hasTeamPinged = useMemo(
    () => messages.some((message) => isTeamPingMessage(message)),
    [messages]
  )

  const latestTeamMessageAt = useMemo(() => {
    let latest: number | null = null
    for (const message of messages) {
      if (message.role === 'customer' || message.role === 'system') continue
      const at = new Date(message.created_at).getTime()
      if (!Number.isFinite(at)) continue
      if (latest == null || at > latest) latest = at
    }
    return latest
  }, [messages])

  const latestExplicitSaraUserAt = useMemo(() => {
    let latest: number | null = null
    for (const message of saraMessages) {
      if (message.role !== 'user') continue

      let content: Record<string, unknown> = {}
      try {
        content =
          typeof message.content === 'string'
            ? JSON.parse(message.content || '{}')
            : ((message.content ?? {}) as Record<string, unknown>)
      } catch {
        content = {}
      }

      const text =
        typeof content.message === 'string' ? content.message.trim() : ''
      if (!text || !isExplicitSaraText(text)) continue

      const at = new Date(message.created_at ?? 0).getTime()
      if (!Number.isFinite(at)) continue
      if (latest == null || at > latest) latest = at
    }
    return latest
  }, [saraMessages])

  const effectiveStickyRecipient = useMemo(
    () =>
      resolveStickyRecipient(
        stickyRecipient,
        latestTeamMessageAt,
        latestExplicitSaraUserAt
      ),
    [latestExplicitSaraUserAt, latestTeamMessageAt, stickyRecipient]
  )

  // Persist the clear so follow-ups stay with the team until the user @Sara again.
  useEffect(() => {
    if (stickyRecipient && !effectiveStickyRecipient) {
      setStickyRecipient(null)
    }
  }, [effectiveStickyRecipient, setStickyRecipient, stickyRecipient])

  const isPrivateNoteMode =
    !isAdmin && !hasTeamPinged && !hasCustomerMessages

  const hasComposerDraft = useMemo(() => {
    const text = stripHtml(draft)
    return text.length > 0 && !isSaraPrefillOnly(text)
  }, [draft])

  const salesLocalNotes = useMemo(
    () => localNotes.filter((note) => note.recipient !== 'sara'),
    [localNotes]
  )

  const isAnonymous = !session?.user?.email
  const quotePublicId = quote?.public_id ?? publicId
  const mockupPreviewUrls = (() => {
    const fromChat = saraMessages.flatMap((message) => {
      if (!isSuccessfulMockupGeneration(message)) return []
      const content = parseSaraMessageContent(message)
      return (content.data?.results ?? []).flatMap(
        (result: { mockup_urls?: string[] }) => result.mockup_urls ?? []
      )
    })
    if (fromChat.length > 0) return fromChat.filter(Boolean).slice(0, 3)

    return (quoteItems ?? [])
      .flatMap((item) =>
        (item.quote_sub_item ?? []).flatMap((sub) => sub.mockup_urls ?? [])
      )
      .filter(Boolean)
      .slice(0, 3)
  })()
  const guestMockupsReady =
    isAnonymous &&
    (saraMessages.some(isSuccessfulMockupGeneration) ||
      mockupPreviewUrls.length > 0)

  const visibleSaraMessages = useMemo(() => {
    const annotated = withSaraMessagePrivacy(saraMessages)
    const forChannel = isAdminChannel
      ? annotated.filter((entry) => entry.isAdmin)
      : annotated.filter((entry) => !entry.isAdmin)
    const forViewer = isAdmin
      ? forChannel.filter((entry) => !entry.isPrivate)
      : forChannel
    if (!isAnonymous) return forViewer
    return forViewer.filter(({ message }) => {
      if (isSuccessfulMockupGeneration(message)) return false
      if (message.role !== 'system') return true
      return parseSaraMessageContent(message).tool_result !== 'quote_crud'
    })
  }, [isAdmin, isAdminChannel, isAnonymous, saraMessages])

  const visibleTeamMessages = isAdminChannel
    ? []
    : (messages as OrderMessageWithUser[])

  const lastSaraUserIsAdmin = useMemo(() => {
    for (let i = saraMessages.length - 1; i >= 0; i--) {
      const message = saraMessages[i]
      if (message.role !== 'user') continue
      return isAdminSaraContent(message.content)
    }
    return false
  }, [saraMessages])

  const inFlightIsAdmin = lastSaraUserIsAdmin || (isSending && isAdminChannel)
  const showSaraLoading =
    (isSaraLoading || isSending) && isAdminChannel === inFlightIsAdmin

  const showPingCta =
    !isAdmin &&
    !hasTeamPinged &&
    isDraftQuoteStatus(quote?.status)

  const activeQuickAction = useMemo((): {
    kind: Exclude<SaraQuickActionKind, 'none'>
    logoUrl?: string
    custom?: { label: string; phrase: string }
  } | null => {
    if (showSaraLoading || isSending) return null

    const groupMessages = getLatestSaraGroupMessages(
      visibleSaraMessages.map(({ message }) => message)
    )
    const fromSara = extractAssistantQuickReply(groupMessages)
    if (fromSara) {
      return {
        kind: 'add_to_order_and_create_mockups',
        custom: fromSara,
      }
    }
    const kind = resolveSaraQuickAction(groupMessages)
    if (kind === 'none') return null
    if (kind === 'search_products' && guestMockupsReady) return null

    if (kind === 'create_mockups_from_logo') {
      const logoUrl = saraGroupFetchedLogoUrls(groupMessages)[0]
      if (!logoUrl) return null
      return { kind, logoUrl }
    }

    return { kind }
  }, [
    guestMockupsReady,
    isSending,
    showSaraLoading,
    visibleSaraMessages,
  ])

  const tabSuggestion = useMemo((): ChatInputTabSuggestion | null => {
    if (!activeQuickAction) return null
    if (activeQuickAction.custom) {
      return {
        hintLabel: activeQuickAction.custom.label,
        insertText: activeQuickAction.custom.phrase,
      }
    }

    const labels = SARA_QUICK_ACTION_LABELS[activeQuickAction.kind]
    const phrases = SARA_QUICK_ACTION_PHRASES[activeQuickAction.kind]

    return {
      hintLabel: t(labels.en, labels.sv),
      insertText: t(phrases.en, phrases.sv),
    }
  }, [activeQuickAction, t])

  const ensureConversationId = useCallback(async () => {
    if (conversationId) return conversationId

    if (!quote?.id || !session?.user?.id) {
      toast({
        variant: 'destructive',
        title: t('Error', 'Fel'),
        description: t(
          'Unable to start this conversation.',
          'Kunde inte starta denna konversation.'
        ),
      })
      return null
    }

    const created = await createConversation.mutateAsync({
      org_id: quote.org_id ?? null,
      user_id: session.user.id,
      quote_id: quote.id,
      name:
        quote.name?.trim() ||
        quote.public_id ||
        t('Order', 'Order'),
    })

    queryClient.setQueryData(
      [queryKeys.conversations, 'byQuote', quote.id],
      created
    )
    setLocalConversationId(created.id)

    return created.id
  }, [
    conversationId,
    createConversation,
    queryClient,
    quote,
    session?.user?.id,
    t,
    toast,
  ])

  const insertMessage = useCallback(
    async (
      activeConversationId: number,
      payload: {
        content: string
        attachmentUrls?: string[]
        role?: 'customer' | 'admin' | 'system'
      }
    ) => {
      const { error } = await supabase.from('order_message').insert({
        conversation_id: activeConversationId,
        role: payload.role ?? (isAdmin ? 'admin' : 'customer'),
        content: payload.content,
        user_id: session?.user?.id ?? null,
        attachment_urls: payload.attachmentUrls ?? [],
        quote_id: quote?.id ?? null,
      })

      if (error) throw error

      if (quote?.id && (payload.role ?? (isAdmin ? 'admin' : 'customer')) === 'customer') {
        trackEvent(supabase, {
          name: 'chat_sent',
          quoteId: quote.id,
        })
      }

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.orderMessages, activeConversationId],
      })
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.conversations],
      })
    },
    [isAdmin, queryClient, quote?.id, session?.user?.id, supabase]
  )

  const persistMessages = useCallback(
    async (
      activeConversationId: number,
      payloads: {
        content: string
        plainText: string
        attachmentUrls?: string[]
      }[]
    ) => {
      for (const payload of payloads) {
        await insertMessage(activeConversationId, {
          content: payload.content,
          attachmentUrls: payload.attachmentUrls,
        })
      }
    },
    [insertMessage]
  )

  const notifyTeam = useCallback(
    (plainTexts: string[]) => {
      const quotePublicId = quote?.public_id ?? publicId
      if (!quotePublicId) return

      const messageBody = plainTexts.filter(Boolean).join('\n\n')

      orderNotifications.mutate({
        kind: 'chat',
        actorRole: 'customer',
        subject:
          t('👋 Customer pinged in order ', '👋 Kund pingade i order ') +
          `#${quotePublicId}`,
        orderNotification: {
          quotePublicId,
          headerBadge: t('Customer ping', 'Kund-ping'),
          headerTitle: t(
            'Customer wants a reply in order chat',
            'Kunden vill ha svar i orderchatten'
          ),
          message: messageBody,
          buttonText: t('View order', 'Se order'),
        },
      })
    },
    [orderNotifications, publicId, quote?.public_id, t]
  )

  const handleSaveNote = useCallback(
    async (content: string, attachmentUrls?: string[], plainText?: string) => {
      const text = plainText?.trim() ?? stripHtml(content)
      if (!text && !attachmentUrls?.length) return

      addLocalNote({
        content: text ? content : '',
        plainText:
          text ||
          t(
            `${attachmentUrls?.length ?? 0} attachment(s)`,
            `${attachmentUrls?.length ?? 0} bilaga(or)`
          ),
        attachmentUrls: attachmentUrls ?? [],
        recipient: 'sales',
      })
      setDraft('')
    },
    [addLocalNote, t]
  )

  const handleSendDirect = useCallback(
    async (content: string, attachmentUrls?: string[], plainText?: string) => {
      if (!content.trim() && !attachmentUrls?.length) return

      try {
        setIsSending(true)

        const activeConversationId = await ensureConversationId()
        if (!activeConversationId) return

        await insertMessage(activeConversationId, {
          content: content || '',
          attachmentUrls,
        })

        const quotePublicId = quote?.public_id ?? publicId
        if (quotePublicId) {
          const messageParts: string[] = []
          if (plainText?.trim()) messageParts.push(plainText.trim())
          if (attachmentUrls?.length) {
            messageParts.push(
              t(
                `${attachmentUrls.length} attachment(s) included.`,
                `${attachmentUrls.length} bilaga(or) bifogade.`
              )
            )
          }

          orderNotifications.mutate({
            kind: 'chat',
            actorRole: isAdmin ? 'admin' : 'customer',
            subject:
              t('💬 New message in order ', '💬 Nytt meddelande i order ') +
              `#${quotePublicId}`,
            orderNotification: {
              quotePublicId,
              headerBadge: t('New message', 'Nytt meddelande'),
              headerTitle: t(
                'New chat message in order',
                'Nytt chattmeddelande i order'
              ),
              message: messageParts.join('\n\n'),
              buttonText: t('View order', 'Se order'),
            },
          })
        }

        setDraft('')
      } catch (err) {
        console.error('Request chat send error:', err)
        toast({
          variant: 'destructive',
          title: t('Failed', 'Misslyckades'),
          description: err instanceof Error ? err.message : undefined,
        })
      } finally {
        setIsSending(false)
      }
    },
    [
      ensureConversationId,
      insertMessage,
      isAdmin,
      orderNotifications,
      publicId,
      quote?.public_id,
      t,
      toast,
    ]
  )

  const handleSubmit = useCallback(
    async (
      content: string,
      attachmentUrls?: string[],
      plainText?: string
    ) => {
      const text = plainText?.trim() ?? stripHtml(content)
      const hasAttachments = (attachmentUrls?.length ?? 0) > 0
      if (!text && !hasAttachments) return

      if (isAdminChannel) {
        const files = (attachmentUrls ?? []).map((url, index) => {
          const extension = url.split('?')[0].split('.').pop()?.toLowerCase()
          const mimeType =
            extension === 'svg'
              ? 'image/svg+xml'
              : extension === 'pdf'
                ? 'application/pdf'
                : 'image/png'

          return {
            url,
            name: `attachment-${index + 1}.${extension || 'png'}`,
            type: mimeType,
          }
        })

        const messageText =
          text ||
          (files.length
            ? t(
                'Place this logo on all products',
                'Placera denna logotyp på alla produkter'
              )
            : '')

        if (!messageText && !files.length) return

        try {
          setIsSending(true)
          await sendToSara(messageText, files.length ? files : undefined, {
            isAdmin: true,
          })
          setDraft('')
        } catch (err) {
          console.error('Request admin Sara send error:', err)
          toast({
            variant: 'destructive',
            title: t('Failed', 'Misslyckades'),
            description: err instanceof Error ? err.message : undefined,
          })
        } finally {
          setIsSending(false)
        }
        return
      }

      let { recipient, nextSticky, cleanedText, isPrivate } =
        detectChatRecipient(text, effectiveStickyRecipient)

      // Attachment-only: keep routing to Sara while that thread is active,
      // but not after the team has taken over (unless sticky/@Sara says so).
      if (
        !text &&
        hasAttachments &&
        recipient === 'sales' &&
        (effectiveStickyRecipient === 'sara' ||
          effectiveStickyRecipient === 'sara_private' ||
          (saraMessages.length > 0 && latestTeamMessageAt == null))
      ) {
        recipient = 'sara'
        nextSticky =
          effectiveStickyRecipient === 'sara_private'
            ? 'sara_private'
            : 'sara'
        cleanedText = ''
        isPrivate = effectiveStickyRecipient === 'sara_private'
      }

      setStickyRecipient(nextSticky)

      if (recipient === 'sara') {
        let files = (attachmentUrls ?? []).map((url, index) => {
          const extension = url.split('?')[0].split('.').pop()?.toLowerCase()
          const mimeType =
            extension === 'svg'
              ? 'image/svg+xml'
              : extension === 'pdf'
                ? 'application/pdf'
                : 'image/png'

          return {
            url,
            name: `attachment-${index + 1}.${extension || 'png'}`,
            type: mimeType,
          }
        })

        if (
          !files.length &&
          activeQuickAction?.kind === 'create_mockups_from_logo' &&
          activeQuickAction.logoUrl &&
          cleanedText === tabSuggestion?.insertText
        ) {
          files = [
            {
              url: activeQuickAction.logoUrl,
              name: 'logo.webp',
              type: 'image/webp',
            },
          ]
        }

        const messageText =
          cleanedText ||
          (files.length
            ? t(
                'Place this logo on all products',
                'Placera denna logotyp på alla produkter'
              )
            : '')

        if (!messageText && !files.length) return

        try {
          setIsSending(true)
          await sendToSara(messageText, files.length ? files : undefined, {
            isPrivate,
          })
          setDraft('')
        } catch (err) {
          console.error('Request Sara send error:', err)
          toast({
            variant: 'destructive',
            title: t('Failed', 'Misslyckades'),
            description: err instanceof Error ? err.message : undefined,
          })
        } finally {
          setIsSending(false)
        }
        return
      }

      if (isPrivateNoteMode) {
        await handleSaveNote(content, attachmentUrls, plainText)
        return
      }

      await handleSendDirect(content, attachmentUrls, plainText)
    },
    [
      activeQuickAction,
      effectiveStickyRecipient,
      handleSaveNote,
      handleSendDirect,
      isAdminChannel,
      isPrivateNoteMode,
      latestTeamMessageAt,
      saraMessages.length,
      sendToSara,
      setStickyRecipient,
      tabSuggestion?.insertText,
      t,
      toast,
    ]
  )

  const handleSaraQuickReply = useCallback(
    (phrase: string) => {
      const trimmed = phrase.trim()
      if (!trimmed || isSending || showSaraLoading) return
      void sendToSara(trimmed, undefined, {
        isPrivate: !isAdminChannel && isPrivateNoteMode,
        isAdmin: isAdminChannel,
      }).catch((err) => {
        console.error('Request Sara quick reply error:', err)
        toast({
          variant: 'destructive',
          title: t('Failed', 'Misslyckades'),
          description: err instanceof Error ? err.message : undefined,
        })
      })
    },
    [
      isAdminChannel,
      isPrivateNoteMode,
      isSending,
      sendToSara,
      showSaraLoading,
      t,
      toast,
    ]
  )

  const handlePingTeam = useCallback(async () => {
    const draftText = stripHtml(draft)
    const draftIsSales =
      hasComposerDraft &&
      detectChatRecipient(draftText, effectiveStickyRecipient).recipient ===
        'sales'

    const pendingFromDraft = draftIsSales
      ? [{ content: draft, plainText: draftText, attachmentUrls: [] as string[] }]
      : []

    const localPayloads = [
      ...salesLocalNotes.map((note) => ({
        content: note.content,
        plainText: note.plainText,
        attachmentUrls: note.attachmentUrls ?? [],
      })),
      ...pendingFromDraft,
    ].filter((payload) => payload.plainText || payload.attachmentUrls.length > 0)

    const existingCustomerTexts = messages
      .filter(
        (message) =>
          message.role === 'customer' && !isTeamPingMessage(message)
      )
      .map((message) => stripHtml(message.content))
      .filter(Boolean)

    try {
      setIsSending(true)

      const activeConversationId = await ensureConversationId()
      if (!activeConversationId) return

      if (localPayloads.length > 0) {
        await persistMessages(activeConversationId, localPayloads)
      }

      await insertMessage(activeConversationId, {
        content: createTeamPingMessageContent(),
        role: 'system',
      })

      const notificationTexts = [
        ...localPayloads.map((payload) => payload.plainText),
        ...existingCustomerTexts,
      ]

      notifyTeam(
        notificationTexts.length
          ? notificationTexts
          : [
              t(
                'Customer pinged and is waiting for a reply.',
                'Kunden pingade och väntar på svar.'
              ),
            ]
      )

      if (quote?.id && isDraftQuoteStatus(quote.status)) {
        await updateQuote.mutateAsync({
          id: quote.id,
          status: CONTACTED_SALES_STATUS,
        })
      }

      clearLocalNotes()
      setDraft('')
      // After an explicit handoff, plain messages go to the team unless @Sara.
      setStickyRecipient(null)
    } catch (err) {
      console.error('Request chat ping error:', err)
      toast({
        variant: 'destructive',
        title: t('Failed', 'Misslyckades'),
        description: err instanceof Error ? err.message : undefined,
      })
    } finally {
      setIsSending(false)
    }
  }, [
    clearLocalNotes,
    draft,
    effectiveStickyRecipient,
    ensureConversationId,
    hasComposerDraft,
    insertMessage,
    messages,
    notifyTeam,
    persistMessages,
    quote?.id,
    quote?.status,
    salesLocalNotes,
    setStickyRecipient,
    t,
    toast,
    updateQuote,
  ])

  if (isQuotePending || (quote?.id != null && isPendingConversation)) {
    return (
      <Column className="flex min-h-0 flex-1 items-center justify-center">
        <p className="text-sm text-gray-500">
          {t('Loading messages...', 'Laddar meddelanden...')}
        </p>
      </Column>
    )
  }

  if (!quote) {
    return (
      <Column className="flex min-h-0 flex-1 items-center justify-center gap-2 px-4 text-center">
        <h2>{t('Quote not found', 'Offert hittades inte')}</h2>
        <p className="text-sm text-gray-600">
          {t(
            'No quote matches this order.',
            'Ingen offert matchar denna order.'
          )}
        </p>
      </Column>
    )
  }

  return (
    <>
      <Column className="relative flex min-h-0 flex-1 flex-col">
        <RequestChatView
          messages={visibleTeamMessages}
          saraMessages={visibleSaraMessages}
          localNotes={
            isAdminChannel || !isPrivateNoteMode ? [] : salesLocalNotes
          }
          isPending={isLoadingMessages}
          isSaraLoading={showSaraLoading}
          quoteId={quote.id}
          flushHeader={isAdmin}
          emptyCopy={
            isAdminChannel
              ? t(
                  'Hidden from the customer. Ask Sara to update the quote, add products, or change mockups.',
                  'Dolt för kunden. Be Sara uppdatera offerten, lägga till produkter eller ändra mockups.'
                )
              : undefined
          }
          onOpenProductOverlay={onOpenProductOverlay}
          onSaraQuickReply={handleSaraQuickReply}
          pingCta={
            !isAdminChannel && showPingCta && !isSaraLoading && !isSending ? (
              <PingKlayderTeamCta
                isPending={
                  isSending || createConversation.isPending || isSaraLoading
                }
                locked={isAnonymous}
                onPing={
                  isAnonymous
                    ? () => setAccountGateOpen(true)
                    : handlePingTeam
                }
              />
            ) : null
          }
          composer={
            <div id={ONBOARDING_ELEMENT_IDS.requestChat}>
              <ChatInputForm
                key={
                  isAdminChannel
                    ? visibleSaraMessages.length === 0
                      ? 'admin-empty'
                      : 'admin'
                    : isPrivateNoteMode
                      ? 'private'
                      : 'direct'
                }
                conversationId={conversationId}
                uploadTargetId={saraChatId ?? conversationId ?? quote.id}
                initialContent={draft}
                onDraftChange={setDraft}
                onSubmit={handleSubmit}
                disabled={!session?.user?.id}
                isPending={isSending || createConversation.isPending}
                isGenerating={showSaraLoading}
                onStop={showSaraLoading ? stopSara : undefined}
                noteMode={isPrivateNoteMode && !isAdminChannel}
                highlightSaraMentions
                prefillSaraMention={
                  isAdminChannel && visibleSaraMessages.length === 0
                }
                tabSuggestion={tabSuggestion}
                channelName={
                  quote.name?.trim() ||
                  quote.public_id ||
                  t('request', 'förfrågan')
                }
                embedded
              />
            </div>
          }
        />
      </Column>

      {isAnonymous && quotePublicId ? (
        <>
          <MockupLeadPopup
            open={guestMockupsReady}
            onOpenChange={() => undefined}
            nextPath={buildAppRequestPath(quotePublicId)}
            previewUrls={mockupPreviewUrls}
          />
          <GuestSignupDialog
            open={accountGateOpen}
            onOpenChange={setAccountGateOpen}
            initialEmail=""
            nextPath={buildAppRequestPath(quotePublicId)}
            title={t('Sign up to continue', 'Skapa konto för att fortsätta')}
            description={t(
              'To continue the conversation and complete the purchase, please create a free account on your work email.',
              'För att fortsätta konversationen och slutföra köpet, skapa ett gratiskonto på din arbetsmejl.'
            )}
          />
        </>
      ) : null}

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {productOverlay && (
              <ProductOverlay
                fullscreen
                orderPanelOpen={false}
                key="request-product-overlay"
                products={productOverlay.products}
                quote_id={productOverlay.quote_id}
                product_categories={productOverlay.product_categories}
                onClose={onCloseProductOverlay}
              />
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  )
}
